import React, {useEffect, useState} from "react";
import {Button, Typography} from "@material-ui/core";
import {connect} from "react-redux";
import CheckIcon from "@material-ui/icons/Check";
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import DocumentViewer from "../../components/DocumentViewer";
import SignIcon from "@material-ui/icons/BorderColor";
import UnlockIcon from "@material-ui/icons/LockOpen";
import FixablyModal from "../../components/FixablyModal";
import Signature from "../../components/Signature";
import {exitReceiveModeRequest, signRequest} from "../../redux/actions/applicationData";
import CircularProgress from "@material-ui/core/CircularProgress";
import Numpad from "../../components/Numpad";
import AdditionalTermsDialog from "../../components/AdditionalTermsDialog";

function ReceiveMode(props) {
    const {
        exitReceiveModeRequest,
        signature,
        firebaseData,
        signRequest,
        signatureSuccessful,
    } = props;

    const [signOpen, setSignOpen] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    // Kept here so closing the dialog keeps them; scoped to one signature id.
    const [termsState, setTermsState] = useState({signatureId: null, entries: []});
    const [exitReceiveModeOpen, setExitReceiveModeOpen] = useState(false);
    const [documentsTitle, setDocumentsTitle] = useState(null);
    const [screensaverActive, setScreensaverActive] = useState(false);
    const [screensaverIndex, setScreensaverIndex] = useState(0);

    const handlePinComplete = (pin) => {
        exitReceiveModeRequest({pin});
    }

    const additionalTerms = signature && Array.isArray(signature.additionalTerms)
        ? signature.additionalTerms
        : [];
    const termEntries = signature && termsState.signatureId === signature.id ? termsState.entries : [];

    const updateTermEntry = (index, entry) => {
        setTermsState((previous) => {
            const entries = previous.signatureId === signature.id ? [...previous.entries] : [];
            entries[index] = entry;
            return {signatureId: signature.id, entries};
        });
    };

    useEffect(() => {
        if (!signature || signatureSuccessful) {
            setTermsState((previous) => previous.signatureId === null
                ? previous
                : {signatureId: null, entries: []});
        }
    }, [signature, signatureSuccessful]);

    useEffect(() => {
        if (signatureSuccessful) {
            return;
        }
        if ((!signature || signature.signed) && termsOpen) {
            setTermsOpen(false);
        }
        if ((!signature || signature.signed) && signOpen) {
            setSignOpen(false);
        }
        if (signature && signature.name && documentsTitle !== signature.name) {
            setDocumentsTitle(signature.name);
        }
    }, [signature, documentsTitle, signOpen, termsOpen, signatureSuccessful]);

    useEffect(() => {
        if (signatureSuccessful) {
            return;
        }
        if ((!firebaseData || !firebaseData.location) && (signOpen || termsOpen)) {
            exitReceiveModeRequest({force: true});
        }
    }, [firebaseData, exitReceiveModeRequest, signOpen, termsOpen, signatureSuccessful]);

    // `firebaseData` is null in INITIAL_STATE and this component can mount before
    // it is ever filled. `Main` schedules its redirects in a setTimeout, but with
    // no stored access token `tokenLogin` calls `doAfter` with no intervening
    // yield (sagas/applicationData.js:33-36), so the loading gate clears inside
    // the same passive-effect flush and this renders before the redirect lands.
    // Reaching /receiveMode with no token is ordinary: a kiosk shortcut straight
    // to the route, or a token a previous failed `tokenLogin` already cleared.
    // (With a token present the failing request is a later task than the timer,
    // so the redirect wins and we never mount - that path is NOT the trigger.)
    const screensavers = firebaseData?.screensavers;
    const screensaverCount = screensavers ? screensavers.length : 0;

    // An `updatedAt` push can shorten the screensaver list while it is cycling,
    // leaving the index past the end of the new list. Reading `mimeType` off the
    // resulting undefined throws. Since FIX-13377 the boundary in `Boot` wraps the
    // router as well as `FirebaseHandler`, so a crash here is caught and bounded
    // rather than unmounting the tree and leaving the tablet blank until someone
    // reloads it by hand - but it still costs the device a reload, and this guard
    // is what stops it happening at all.
    useEffect(() => {
        if (screensaverCount > 0 && screensaverIndex >= screensaverCount) {
            setScreensaverIndex(0);
        }
    }, [screensaverCount, screensaverIndex]);

    useEffect(() => {
        let timer;
        if (screensaverCount === 0) {
            return;
        }
        const resetTimer = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setScreensaverActive(true), 10000); // 10s
            setScreensaverActive(false); // hide screensaver on activity
        };
        const events = ['keydown', 'touchstart', 'mousemove', 'mousedown'];
        events.forEach(e => window.addEventListener(e, resetTimer));

        resetTimer(); // start timer initially

        return () => {
            clearTimeout(timer);
            events.forEach(e => window.removeEventListener(e, resetTimer));
        };
        // Mount-once by design: this attaches four window listeners and owns the
        // idle timer. Listing screensaverCount would detach and reattach them,
        // and reset the idle countdown, whenever the screensaver list changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!screensaverActive || screensaverCount <= 1) return;

        const currentItem = screensavers[screensaverIndex];
        if (!currentItem) return;

        let timer;

        if (currentItem.displayTime !== null && currentItem.mimeType !== 'video/mp4' && currentItem.mimeType !== 'video/quicktime') {
            const duration = currentItem.displayTime * 1000;
            timer = setTimeout(() => {
                setScreensaverIndex((prev) => (prev + 1) % screensaverCount);
            }, duration);
        }

        return () => clearTimeout(timer);
    }, [screensaverActive, screensaverIndex, screensavers, screensaverCount]);

    const renderScreensaver = () => {
        if (screensaverCount === 0) {
            return <div>Loading screensaver...</div>;
        }

        // The index can still point past the end for the render that happens
        // between a shortened list arriving and the reset effect firing.
        const item = screensavers[screensaverIndex] || screensavers[0];
        if (!item) {
            return <div>Loading screensaver...</div>;
        }

        return (
            <div style={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'black',
                color: 'white',
            }}>
                {item.mimeType !== 'video/mp4' && item.mimeType !== 'video/quicktime' ? (
                    <img id={`screensaver`} src={item.url} alt="" style={{ maxHeight: '100%', maxWidth: '100%' }} />
                ) : (
                    <video
                        src={item.url}
                        autoPlay
                        muted
                        style={{ maxHeight: '100%', maxWidth: '100%' }}
                        loop={screensaverCount === 1}
                        onEnded={() => {setScreensaverIndex((prev) => (prev + 1) % screensaverCount)}}
                    />
                )}
            </div>
        );
    }
    const renderDocument = () => {
      return (
        <div style={{ flex: 1, height: "100%", width: "100%" }}>
          <div
            style={{
              padding: 12,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ flex: 1 }}>
              <DocumentViewer
                uri={signature.documentFileUrl}
                pdf={signature.documentFilePdf}
                renderFooter={() => (
                  <div style={{ display: "flex" }}>
                    <Button
                      style={{ marginTop: 12, marginRight: 12 }}
                      variant="outlined"
                      color="default"
                      onClick={() => setExitReceiveModeOpen(true)}
                      endIcon={<UnlockIcon />}
                    >
                      Exit receive mode
                    </Button>
                    {signature.signed ? (
                      <Button
                        style={{ marginTop: 12, flex: 1 }}
                        variant="outlined"
                        disabled
                        color="primary"
                        onClick={() => {}}
                        endIcon={<CheckIcon />}
                      >
                        ({signature.name}) Signed by {signature.signer}
                      </Button>
                    ) : (
                      <Button
                        style={{ marginTop: 12, flex: 1 }}
                        variant="contained"
                        color="secondary"
                        onClick={() => additionalTerms.length > 0 ? setTermsOpen(true) : setSignOpen(true)}
                        endIcon={<SignIcon />}
                      >
                        ({signature.name}) Sign document(s) as{" "}
                        {signature.signer}
                      </Button>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      );
    };

    const renderReceiveMode = () => {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                flexDirection: 'column'
            }}>
                <CircularProgress color="primary"/>
                <br/>
                Ready to receive documents...
                <Button
                    style={{marginTop: 12, marginRight: signature ? 12 : 0}}
                    variant='outlined'
                    color='default'
                    onClick={() => setExitReceiveModeOpen(true)}
                    endIcon={<UnlockIcon/>}>
                    Exit receive mode
                </Button>
            </div>
        );
    }

    const renderSignatureSuccessful = () => {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <CheckCircleOutlineRoundedIcon color={'primary'} style={{fontSize: 64, marginRight: 12}}/>
                <Typography color={'primary'} variant={'h3'}>Document(s) signed successfully</Typography>
            </div>
        )
    }

    const onSign = ({signaturePad, doAfter, marketingOptIn}) => {
        const base64png = signaturePad.toDataURL();
        const additionalTermsInitials = additionalTerms.map((_, index) => termEntries[index] && termEntries[index].dataUrl);
        signRequest({identifier: signature.id, fileData: base64png, marketingOptIn, additionalTermsInitials, doAfter});
    }

    const signer = signature && signature.signer ? signature.signer : '';

    return !signatureSuccessful ? (
      <div style={{ height: "100%", width: "100%" }}>
          {signature
              ? renderDocument()
              : screensaverActive
                  ? renderScreensaver()
                  : renderReceiveMode()}
        <AdditionalTermsDialog
          open={termsOpen}
          terms={additionalTerms}
          labels={signature && signature.additionalTermsLabels}
          entries={termEntries}
          onChange={updateTermEntry}
          onClose={() => setTermsOpen(false)}
          onContinue={() => {
            setTermsOpen(false);
            setSignOpen(true);
          }}
        />
        <FixablyModal
          title={`Sign "${documentsTitle}" as ${signer}`}
          open={signOpen}
          onClose={() => setSignOpen(false)}
        >
          <Signature
            // Remount whenever the pending signature changes so the consent
            // checkboxes never carry over from a previous document/customer.
            key={signature && signature.id}
            disclaimer={
              signature && signature.disclaimer ? signature.disclaimer : null
            }
            enableTermsCheckbox={!!(signature && signature.enableTermsCheckbox)}
            enableMarketingOptIn={!!(signature && signature.enableMarketingOptIn)}
            termsCheckboxLabel={signature && signature.termsCheckboxLabel}
            marketingOptInLabel={signature && signature.marketingOptInLabel}
            onSign={onSign}
          />
        </FixablyModal>
        <Numpad
          label={"Pin"}
          type={"password"}
          titleIcon={<UnlockIcon />}
          title={"Exit receive mode"}
          open={exitReceiveModeOpen}
          onClose={() => setExitReceiveModeOpen(false)}
          onDone={(data) => handlePinComplete(data)}
        />
      </div>
    ) : (
      renderSignatureSuccessful()
    );
}

export default connect(({applicationData}) => ({
    firebaseData: applicationData.firebaseData,
    signature: applicationData.signature,
    signatureSuccessful: applicationData.signatureSuccessful,
}), {exitReceiveModeRequest, signRequest})(ReceiveMode);
